import { useEffect, useRef } from 'react';

import { useAnalytics } from '@src/features/analytics/hooks';
import { resolvePrimaryGuestIdentity } from '@src/features/analytics/utils/resolve-primary-guest-identity';
import { Trip } from '@src/types/api/models/trip';

type IdentifyPrimaryGuestProps = {
  guests?: Trip['guests'];
  hasProfileIdentity: boolean;
};

// Module-level, not a ref: the layouts mounting this component remount on every client-side
// navigation, and identification must run once per guest, not once per navigation.
const identifiedPersonIds = new Set<string>();

/**
 * Sends the Reservation-derived analytics identity for a Key-authenticated guest, mounted by the
 * shared layouts so it covers every reservation-bearing route. The rule lives in
 * `resolvePrimaryGuestIdentity`: a Profile identity wins by presence, and the traits match what
 * Profile identification sends.
 */
export function IdentifyPrimaryGuest({ guests, hasProfileIdentity }: IdentifyPrimaryGuestProps) {
  const { trackIdentification } = useAnalytics();
  // useAnalytics returns a fresh function per render; the ref keeps it out of the effect deps, so
  // the effect re-runs only when the reservation data changes — not on every layout render.
  const trackIdentificationRef = useRef(trackIdentification);
  trackIdentificationRef.current = trackIdentification;

  useEffect(() => {
    const identity = resolvePrimaryGuestIdentity({ hasProfileIdentity, guests });

    if (!identity || identifiedPersonIds.has(identity.personId)) {
      return;
    }

    identifiedPersonIds.add(identity.personId);
    trackIdentificationRef
      .current({
        guestId: identity.personId,
        email: identity.email,
        firstName: identity.firstName,
        lastName: identity.lastName,
      })
      .catch((error) => {
        console.error('Failed to identify user:', error);
      });
  }, [guests, hasProfileIdentity]);

  return null;
}
